import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { AssetVizMode } from '@asset-viz/defines'
import { useSurfaceProps } from '../surfaces/util/SurfaceProvider'
import { ClipConfig } from '@asset-viz/systems/clipConfig'
import {
  HtmlOverlayAnchor,
  HtmlOverlayPriority,
  Vec3,
} from '@asset-viz/utils/helpers'
import { useAssetVizLocale } from '@asset-viz/providers/locale'
import React from 'react'
import styled, { css, RuleSet } from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { HtmlOverlay3D } from './HtmlOverlay3D'

const StyledLabel = styled.div<{
  $cssRules: RuleSet<object>
  $color?: string
  $largeText?: boolean
}>`
  display: flex;
  min-width: 2rem;
  line-height: 1;
  user-select: none;
  pointer-events: none;
  ${({ $color }) => $color && `color: ${$color};`}
  ${({ $largeText }) => $largeText && `font-size: 2em;`}
  ${({ $cssRules }) => $cssRules};
`

export type TicksConfig = {
  measurementUnit: GeckoMeasurementUnit
  includeStart: boolean
  includeTicks: boolean
  includeEnd: boolean
  tickInterval: number
  tickStart: GeckoMeasurement
  tickEnd: GeckoMeasurement
}

/**
 * A component used to line up a number of measurement labels in 3D space
 * @param start               The measurement to start at (i.e. "0 ft")
 * @param end                 The measurement to end at (i.e. "0 ft")
 * @param includeStart        Whether to include a label for the start point
 * @param includeEnd          Whether to include a label for the end point
 * @param includeTicks        Whether to include intermediate tick marks
 * @param anchor              The anchor point for ruler ticks
 * @param getTickPosition     A function for calculating 3D tick position
 */
export const Ruler3D: React.FC<{
  start: GeckoMeasurement
  end: GeckoMeasurement
  includeTicks?: boolean
  includeStart?: boolean
  includeEnd?: boolean
  anchor?: HtmlOverlayAnchor
  getTickPosition: (coord: GeckoMeasurement) => Vec3
  fullTicksConfig?: TicksConfig
}> = ({
  start,
  end,
  includeStart,
  includeEnd,
  includeTicks,
  anchor,
  getTickPosition,
  fullTicksConfig,
}) => {
  //----------------------------------------------------------------------------

  const { getPreferredMeasurementUnit } = useAssetVizLocale()
  const { showRulers, clipConfig } = useAssetVizInternal()
  const { componentModel } = useSurfaceProps()

  const showThisRuler = React.useMemo(() => {
    if (showRulers === false || showRulers === true) return showRulers
    return showRulers(componentModel)
  }, [componentModel, showRulers])

  if (!showThisRuler) return null

  if (!fullTicksConfig) {
    const measurementUnit = getPreferredMeasurementUnit(start.unit)
    const intervalSize = getIntervalSizeForMeasurementUnit(measurementUnit)
    const tickStart = start.convertTo(measurementUnit)
    const tickEnd = end.convertTo(measurementUnit)
    const tickInterval = Math.sign(end.minus(start).num) * intervalSize

    fullTicksConfig = {
      measurementUnit,
      tickInterval,
      tickStart,
      tickEnd,
      includeStart: includeStart ?? false,
      includeEnd: includeEnd ?? false,
      includeTicks: includeTicks ?? false,
    }
  }

  return (
    <RulerFromTicksConfig
      start={start}
      end={end}
      ticksConfig={fullTicksConfig}
      anchor={anchor ?? 'center'}
      clipConfig={clipConfig}
      getTickPosition={getTickPosition}
    />
  )
}

const RulerFromTicksConfig: React.FC<{
  start: GeckoMeasurement
  end: GeckoMeasurement
  ticksConfig: TicksConfig
  anchor: HtmlOverlayAnchor
  clipConfig: ClipConfig
  getTickPosition: (coord: GeckoMeasurement) => Vec3
}> = ({ start, end, ticksConfig, anchor, clipConfig, getTickPosition }) => {
  const { featureLabelColorString, invertColors, mode } = useAssetVizInternal()
  const largeText = invertColors && mode === AssetVizMode.Screenshot
  const tickMarks = React.useMemo(() => {
    const result = new Array<GeckoMeasurement>()
    const {
      measurementUnit,
      includeStart,
      includeTicks,
      includeEnd,
      tickInterval,
      tickStart,
      tickEnd,
    } = ticksConfig
    if (includeTicks && !!tickInterval) {
      for (
        let v =
          Math.floor(tickStart.num / tickInterval) * tickInterval +
          tickInterval;
        end.num > start.num ? v < tickEnd.num : v > tickEnd.num;
        v += tickInterval
      )
        result.push(GeckoMeasurement.CreateFromValue(v, measurementUnit))
    }

    const dropInterval = Math.abs(tickInterval / 4)
    if (includeEnd) {
      const beforeEnd = result.at(-1)
      if (beforeEnd && tickEnd.minus(beforeEnd).abs().num <= dropInterval)
        result.pop()
      result.push(tickEnd)
    }
    if (includeStart) {
      const afterStart = result.at(0)
      if (afterStart && tickStart.minus(afterStart).abs().num <= dropInterval)
        result.shift()
      result.unshift(tickStart)
    }
    return result.map((coord, i) => {
      const position = getTickPosition(coord)
      if (clipConfig.rangeX && (anchor === 'left' || anchor === 'right')) {
        position[0] = Math.max(
          clipConfig.rangeX[0],
          Math.min(position[0], clipConfig.rangeX[1])
        )
        if (clipConfig.fullRangeX) {
          position[0] =
            position[0] /
              (clipConfig.fullRangeX[1] - clipConfig.fullRangeX[0]) +
            0.05
        }
      }
      if (clipConfig.rangeY && (anchor === 'top' || anchor === 'bottom')) {
        position[1] = Math.max(
          clipConfig.rangeY[0],
          Math.min(position[1], clipConfig.rangeY[1])
        )
        if (clipConfig.fullRangeY) {
          position[1] =
            position[1] /
              (clipConfig.fullRangeY[1] - clipConfig.fullRangeY[0]) +
            0.05
        }
      }
      const isFirstOrLast = i === 0 || i === result.length - 1
      const priority: HtmlOverlayPriority =
        isFirstOrLast || i % 10 === 0
          ? HtmlOverlayPriority.RulerTickMedium
          : i % 5 === 0
            ? HtmlOverlayPriority.RulerTickMedium
            : HtmlOverlayPriority.RulerTickLow
      const maxPixelOverlap = isFirstOrLast ? 8 : 0
      const cssRules = getCssRulesForAnchor(anchor)
      return {
        key: coord.toString(),
        coord,
        x: position[0],
        y: position[1],
        z: position[2],
        priority,
        cssRules,
        maxPixelOverlap,
      }
    })
  }, [
    anchor,
    start,
    end,
    ticksConfig,
    clipConfig.rangeX,
    clipConfig.rangeY,
    clipConfig.fullRangeX,
    clipConfig.fullRangeY,
    getTickPosition,
  ])

  const { formatMeasurement } = useAssetVizLocale()

  //----------------------------------------------------------------------------

  return (
    <>
      {tickMarks.map(
        ({ key, coord, x, y, z, priority, cssRules, maxPixelOverlap }) => {
          return (
            <HtmlOverlay3D
              key={key}
              x={x}
              y={y}
              z={z}
              anchor={anchor}
              priority={priority}
              maxPixelOverlap={maxPixelOverlap}>
              <StyledLabel
                $cssRules={cssRules}
                $color={featureLabelColorString}
                $largeText={largeText}>
                {formatMeasurement({
                  measurement: coord,
                  variant: 'geometric',
                })}
              </StyledLabel>
            </HtmlOverlay3D>
          )
        }
      )}
    </>
  )
}

//------------------------------------------------------------------------------

const getIntervalSizeForMeasurementUnit = (
  measurementUnit: GeckoMeasurementUnit
) => {
  if (measurementUnit === GeckoMeasurementUnit.Tubes) return 50
  if (measurementUnit === GeckoMeasurementUnit.Degrees) return 10
  if (measurementUnit === GeckoMeasurementUnit.Inches) return 12 * 10
  if (measurementUnit === GeckoMeasurementUnit.Millimeters) return 1000 * 10
  return 10
}

export const getCssRulesForAnchor = (
  anchor: HtmlOverlayAnchor
): RuleSet<object> => {
  switch (anchor) {
    case 'top':
      return css`
        flex-direction: column;
        align-content: center;
        offset: -0.5px 0;
        text-align: center;
        padding: 0.5rem 0 0 0;
      `
    case 'bottom':
      return css`
        flex-direction: column-reverse;
        align-content: center;
        text-align: center;
        padding: 0 0 0.5rem 0;
      `
    case 'left':
      return css`
        flex-direction: row;
        align-content: center;
        text-align: left;
        padding: 0 0 0 0.5rem;
      `
    case 'right':
      return css`
        flex-direction: row-reverse;
        align-content: center;
        text-align: right;
        padding: 0 0.5rem 0 0;
      `
    default:
      return css`
        flex-direction: row;
        align-content: center;
        text-align: center;
        padding: 0 0.25rem 0 0.25rem;
      `
  }
}
